import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getCourseById, getFreeVideos } from "../../../../redux/features/course/courseService";
import PaymentForm from "../../PayhereCheckoutForm/PaymentForm";
import styles from "./index.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faFileVideo } from "@fortawesome/free-regular-svg-icons";
import {
  faCalendar,
  faChevronRight,
  faClock,
  faFilePdf,
  faMoneyBill,
  faUser,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { selectUserDetails } from "../../../../redux/features/auth/authSlice";
import Skeleton from "react-loading-skeleton";
import ReactPlayer from "react-player";
import { createCoursePurchaseRequest } from "../../../../redux/features/order/orderServices";
import BeatLoader from "react-spinners/BeatLoader";
import { Bounce, toast } from "react-toastify";
import { createLookup } from "../../../../redux/features/lookup/lookupService";
import CourseSEO from "../../../../components/SEO/CourseSEO/CourseSEO";

const bankDetails = [
  { label: "Account Name", value: "Ardillalabs (PVT) LTD" },
  { label: "Account No", value: "120214034059" },
  { label: "Bank", value: "Sampath Bank" },
  { label: "Branch", value: "Delgoda" },
];

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const CourseGenAI = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const { slug } = useParams();

  const { isCourseLoading, courseDetails, lessons } = useSelector(
    (state) => state.course.course
  );

  const { isLoggedIn } = useSelector(selectUserDetails);

  const [expandedLessons, setExpandedLessons] = useState({});
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewVideos, setPreviewVideos] = useState([]);
  const [selectedPreview, setSelectedPreview] = useState(null);

  useEffect(() => {
    dispatch(getCourseById({ slug }))
      .finally(() => {
        fetch("https://ipapi.co/json/")
          .then((res) => res.json())
          .then((data) => {
            const lookupData = {
              city: data?.city,
              country_code: data?.postal,
              country_name: data?.country_name,
              region: data?.region,
              timezone: data?.timezone,
            }
            dispatch(createLookup(lookupData))
          });
      });
  }, [dispatch, slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
  if (lessons?.length > 0) {
    const expanded = lessons.reduce((acc, lesson) => {
      acc[lesson.id] = true;
      return acc;
    }, {});

    setExpandedLessons(expanded);
  }
}, [lessons]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setPreviewModalOpen(false); // close modal
      }
    };

    if (isPreviewModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPreviewModalOpen]);

  const toggleDetails = (id) => {
    setExpandedLessons((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openLogin = () => {
    navigate("/auth/login");
  };

  // Preview
  const getPreviewVideos = async (videoId) => {
    const response = await dispatch(getFreeVideos({ slug })).unwrap();
    setPreviewVideos(response);
    const selected = response.find((video) => video.id === videoId);
    setSelectedPreview(selected);
  }

  // Payment options
  const fileInputRef = useRef(null);

  const [paymentType, setPaymentType] = useState("");
  const [paymentTypeError, setPaymentTypeError] = useState("");
  const [paySlip, setPaySlip] = useState(null);
  const [paySlipError, setPaySlipError] = useState("");
  const [disabledBtn, setDisabledBtn] = useState(false);

  const validateFile = (image) => {
    if (image) {
      if (!image.type.startsWith("image/")) {
        setPaySlipError("Only image files are allowed.");
        return;
      }

      // if (thumbnail.size > MAX_FILE_SIZE) {
      //     setErrors({ ...errors, file: "File size exceeds 2MB limit." });
      //     return;
      // }

      setPaySlipError("");
      setPaySlip(image);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const selectedFile = event.dataTransfer.files[0];

    if (selectedFile) {
      validateFile(selectedFile);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      validateFile(selectedFile);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const showToast = (type, message) => {
    const options = {
      position: "top-right",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      style: {
        width: "500px",          // 🔥 increase width
        minHeight: "200px",       // 🔥 increase height
        padding: "20px",
        borderRadius: "12px",
        fontSize: "16px",
      },
    };

    if (type === "success") {
      toast.success(message, options);
    } else if (type === "error") {
      toast.error(message, options);
    }
  };

  const submitPayment = async () => {
    if (!paySlip) {
      setPaySlipError("Payment slip is required.")
    }
    try {
      setDisabledBtn(true);
      const date = new Date();
      const formData = new FormData();
      formData.append("course_id", courseDetails?.id);
      formData.append("paid_date", date);
      formData.append("image", paySlip);

      const response = await dispatch(
        createCoursePurchaseRequest(formData)
      ).unwrap();
      if (response.success) {
        setDisabledBtn(false);
        setPaymentType("");
        setPaySlip(null);
        showToast('success', 'Your registration request has been successfully submitted. Please allow few hours for the approval process to be completed. You will receive a notification once your request has been reviewed. අප සමඟ සම්බන්ධ වීම පිළිබඳව ස්තූතියි! ඔබගේ ලියාපදිංචි වීමේ අයදුම්පත සාර්ථකව ලැබුණි. අනුමත කිරීමේ ක්‍රියාවලිය සම්පූර්ණ වූ වහාම අපි ඔබට දැනුම් දෙන්නෙමු.')
      }
    } catch (error) {
      setDisabledBtn(false);
      showToast('error', 'An error occurred while submitting your registration form. Please check your internet connection and try again. If the issue persists, please contact our support team. ඔබගේ අයදුම්පත ඉදිරිපත් කිරීමේදී දෝෂයක් සිදු විය. කරුණාකර ඔබගේ අන්තර්ජාල සබඳතාව පරීක්ෂා කර නැවත උත්සාහ කරන්න. ගැටලුව දිගටම පවතී නම්, අපගේ සහාය කණ්ඩායම අමතන්න.')
    }
  }

  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);

    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const submitWhatsApp = () => {
    window.open('https://wa.me/94779769012')
  }

  const openMentor = (id) => {
    window.open(
      `${window.location.origin}/mentors/${id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const courseUrl = `/courses/${slug}`;

  const highlights = [
    {
      title: "Beginner-Friendly",
      text: "No prior AI experience is required. The journey starts with the fundamentals and gradually builds confidence.",
    },
    {
      title: "Hands-on Practice",
      text: "Learn through step-by-step lessons, practical demos, and real-world AI projects.",
    },
    {
      title: "Sinhala Language",
      text: "මෙම පාඨමාලාවේ සියලුම වීඩියෝ සරල සිංහල භාෂාවෙන් පැහැදිලි කර ඇත.",
    },
  ];

  const learningPoints = [
    "Artificial Intelligence, Machine Learning, & Deep Learning පිළිබඳ මූලික අවබෝධය ලබා ගැනීම",
    "Supervised, unsupervised, reinforcement, සහ self-supervised learning යන ක්‍රමවේදයන් හඳුනා ගැනීම",
    "AI Coding වලට ඕන කරන Python සරලව ඉගෙන ගැනීම",
    "Google Colab භාවිතා කරමින් Machine Learning models නිර්මාණය කිරීම",
    "Image classification and sentiment analysis models නිර්මාණය කිරීම",
    "Transformer Architecture සහ Large Language Models (LLMs) ක්‍රියා කරන ආකාරය අවබෝධය කර ගැනීම",
    "Hugging Face models සමඟ ප්‍රායෝගිකව වැඩ කිරීම",
    "OpenAI API එක භාවිත කරන ආකාරය ඉගෙන ගැනීම",
    "Tokenization and embeddings පිළිබඳව ඉගෙන ගැනීම",
    "LangChain භාවිතා කරමින් AI Applications නිර්මාණය කිරීම",
    "ChromaDB ඇසුරින් vector databases ගොඩනැඟීම",
    "RAG (Retrieval-Augmented Generation) තාක්ෂණය සහිත යෙදුම් නිර්මාණය කිරීම",
    "Prompt engineering techniques ප්‍රගුණ කිරීම",
  ];

  const modules = [
    "Artificial Intelligence Fundamentals",
    "Machine Learning & Deep Learning",
    "Transformer Architecture",
    "Generative AI",
    "Hugging Face",
    "OpenAI",
    "LangChain & Vector Databases",
    "Retrieval-Augmented Generation (RAG)",
    "Prompt Engineering",
  ];

  const projects = [
    "Loan Approval Prediction using Machine Learning",
    "Image Classification using CNN",
    "Sentiment Analysis using RNN",
    "OpenAI API Integration",
    "AI Image Generation",
    "Document Embedding System",
    "Semantic Search using Embeddings",
    "ChromaDB Vector Database",
    "RAG Chatbot",
    "Prompt Engineering Exercises",
  ];

  const technologies = [
    {
      title: "AI & Machine Learning",
      items: ["Artificial Intelligence", "Machine Learning", "Deep Learning", "CNN", "RNN", "Transformers", "Large Language Models (LLMs)"],
    },
    {
      title: "Frameworks",
      items: ["Hugging Face", "OpenAI API", "LangChain"],
    },
    {
      title: "Databases & Search",
      items: ["ChromaDB", "Vector Databases", "Embeddings", "Tokenization"],
    },
    {
      title: "Development Tools",
      items: ["Python", "Google Colab", "VS Code"],
    },
  ];

  const audiences = [
    "University Students",
    "Software Engineers",
    "Data Analysts",
    "AI Beginners",
    "Business Professionals",
    "Content Creators",
    "Startup Founders",
    "Anyone Curious About AI",
  ];

  const faqs = [
    {
      question: "Do I need programming experience?",
      answer: "Basic programming knowledge is helpful, but the course is explained step by step and is suitable for beginners.",
    },
    {
      question: "Is this course suitable for complete beginners?",
      answer: "Yes. It starts with AI fundamentals and gradually moves into advanced Generative AI topics.",
    },
    {
      question: "Will I build real projects?",
      answer: "Yes. You will complete practical projects throughout the course, including AI integrations and RAG applications.",
    },
    {
      question: "Will I receive a certificate?",
      answer: "Yes. You will receive a course completion certificate after completing the course requirements.",
    },
    {
      question: "How do I purchase a course?",
      answer: 'For detailed step-by-step instructions on how to register and purchase courses, visit our <a href="/how-to-purchase" style={{color: "var(--secondary)", textDecoration: "none", fontWeight: "600"}}>How to Purchase guide</a>. It covers registration, payment methods, and FAQs about the purchasing process.',
      isHtml: true,
    },
  ];

  return (
    <div>
      {isCourseLoading ? (
        <>
          <section className={styles.A}>
            <div className="main-container">
              <div className={`container-content ${styles.headerContent}`}>
                <p className={styles.title}><Skeleton width={600} /></p>
                <Skeleton width={220} height={24} />
                <p className={styles.auther}>
                  <Skeleton width={360} height={20} />
                </p>
                <div className={styles.detailsRow}>
                  <p className={styles.detailsP}>
                    <Skeleton width={180} height={20} />
                  </p>
                  <p className={styles.detailsP}>
                    <Skeleton width={180} height={20} />
                  </p>
                </div>
              </div>
            </div>
          </section>
          <div className="main-container">
            <div className="container-content">
              <div className={styles.layout}>
                <div className={styles.C}>
                  <div className={styles.skeletonCard}>
                    <Skeleton width={170} height={14} />
                    <Skeleton width="70%" height={30} className={styles.skeletonTitle} />
                    <div className={styles.skeletonTextBlock}>
                      <Skeleton height={16} />
                      <Skeleton height={16} />
                      <Skeleton width="85%" height={16} />
                    </div>
                    <div className={styles.skeletonHighlightGrid}>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div className={styles.skeletonHighlightItem} key={index}>
                          <Skeleton width="60%" height={18} />
                          <Skeleton height={14} />
                          <Skeleton height={14} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCard}>
                    <Skeleton width={180} height={14} />
                    <div className={styles.skeletonList}>
                      {Array.from({ length: 8 }).map((_, index) => (
                        <Skeleton key={index} height={16} />
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCard}>
                    <Skeleton width={180} height={18} />
                    <div className={styles.courseContentDetails}>
                      <Skeleton width={110} height={14} />
                      <Skeleton width={110} height={14} />
                      <Skeleton width={130} height={14} />
                    </div>
                    <div className={styles.skeletonLessonList}>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div className={styles.skeletonLessonItem} key={index}>
                          <Skeleton height={22} width="70%" />
                          <Skeleton height={16} width="90%" />
                          <Skeleton height={16} width="85%" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCard}>
                    <Skeleton width={220} height={18} />
                    <div className={styles.skeletonProjectGrid}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <Skeleton key={index} height={56} borderRadius={14} />
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCard}>
                    <Skeleton width={220} height={18} />
                    <div className={styles.skeletonTechGrid}>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} height={100} borderRadius={14} />
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCard}>
                    <Skeleton width={220} height={18} />
                    <div className={styles.skeletonFaqList}>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div className={styles.skeletonFaqItem} key={index}>
                          <Skeleton height={18} width="60%" />
                          <Skeleton height={14} />
                          <Skeleton height={14} width="85%" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.skeletonCtaSection}>
                    <Skeleton width="60%" height={24} />
                    <Skeleton width="80%" height={16} />
                    <Skeleton width={140} height={44} />
                  </div>
                </div>

                <aside className={styles.B}>
                  <Skeleton height={220} width="100%" borderRadius={8} />
                  <div className={styles.skeletonTextBlock}>
                    <Skeleton height={14} />
                    <Skeleton height={14} />
                    <Skeleton height={14} />
                    <Skeleton width="80%" height={14} />
                  </div>
                  <div className={styles.skeletonSidebarActions}>
                    <Skeleton height={44} />
                    <Skeleton height={44} />
                    <Skeleton height={44} />
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <CourseSEO
            title={`${courseDetails?.title} | Studyhub`}
            description={
              courseDetails?.description?.substring(0, 160) || "Learn with Studyhub"
            }
            keywords={courseDetails?.sub_titles?.map((subtitle) => (subtitle?.title))}
            author={courseDetails?.user?.full_name}
            image={courseDetails?.image_url}
            url={courseUrl}
            type="Course"
            structuredData={{
              "@context": "https://schema.org",
              "@type": "Course",
              name: courseDetails?.title,
              description: courseDetails?.description,
              image: courseDetails?.image_url,
              provider: {
                "@type": "Organization",
                name: "Studyhub",
                url: "https://www.studyhub.lk",
              },
            }}
          />
          <section className={styles.A}>
            <div className="main-container">
              <div className={`container-content ${styles.headerContent}`}>
                <p className={styles.title}>{courseDetails?.title}</p>

                <p className={styles.imageLabel}>
                  {courseDetails?.course_category?.name}
                </p>

                <p className={styles.auther}>
                  Created by <span style={{ textDecoration: 'underline' }} onClick={() => openMentor(courseDetails?.user?.id)}>{courseDetails?.user?.full_name}</span>
                </p>

                <div className={styles.detailsRow}>
                  <p className={styles.detailsP}>
                    <FontAwesomeIcon icon={faUser} />
                    Enrolls {courseDetails?.download}
                  </p>
                  <p className={styles.detailsP}>
                    <FontAwesomeIcon icon={faMoneyBill} />
                    Rs. {courseDetails?.discount_price > 0 ? formatNumber(courseDetails?.discount_price) : formatNumber(courseDetails?.price)}
                    <span style={{ textDecoration: 'line-through', color: '#5B5A5A' }}>Rs. {courseDetails?.discount_price > 0 && courseDetails?.price?.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            </div>
          </section>
          <div className="main-container">
            <div className="container-content">
              <div className={styles.layout}>

                {/* LEFT - C */}
                <div className={styles.C}>
                  <section className={styles.sectionCard}>
                    <p className={styles.sectionEyebrow}>Course Overview</p>
                    <h2 className={styles.sectionTitle}>Master Generative AI from Scratch and Build Real AI Applications</h2>
                    <p className={styles.sectionText}>
                      ඔබ ශිෂ්‍යයෙක්, Software Developer හෝ AI පිළිබඳ උනන්දුවක් දක්වන අයෙක් නම්, මෙම පාඨමාලාව මඟින් ඔබට AI හි මූලික කරුණුවල සිට OpenAI, Hugging Face, LangChain, සහ RAG භාවිතා කරමින් නවීන AI Application නිර්මාණය කිරීම දක්වා සියල්ල කියා දෙයි.
                      විශේෂයෙන්ම, AI Application වලට පිවිසීමට පෙර ඔබට අවශ්‍ය වන මූලික Python දැනුම ලබා දෙන විශේෂ වීඩියෝ පාඩම් මාලාවක් ද මීට ඇතුළත් කර ඇත.
                    </p>
                    <p className={styles.sectionText}>
                      මෙම Pre-recorded පාඨමාලාව නිර්මාණය කර ඇත්තේ නිදහසේ ඉගෙනීමට කැමති ඔබ වෙනුවෙන්මයි. විෂය කරුණු ඉතා පැහැදිලිව සහ පියවරෙන් පියවර ඉදිරිපත් කර ඇති බැවින්, කිසිදු බාධාවකින් තොරව ස්වයංව අධ්‍යයනය කළ හැකියි. එක් වරක් ලියාපදිංචි වී, සියලුම වීඩියෝ පාඩම් ඔබට පහසු ඕනෑම වේලාවක, ඕනෑම වාර ගණනක් නැරඹීමේ පූර්ණ නිදහස ලබා ගන්න.
                    </p>
                    <div className={styles.highlightGrid}>
                      {highlights.map((item) => (
                        <div className={styles.highlightCard} key={item.title}>
                          <h3>{item.title}</h3>
                          <p>{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                  <section className={styles.sectionCard} style={{ marginTop: '18px' }}>
                    <p className={styles.sectionEyebrow}>What You Will Learn</p>
                    <ul className={styles.bulletList}>
                      {learningPoints.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </section>
                  <p className={styles.courseContent}>Course Content</p>
                  <div className={styles.courseContentDetails}>
                    <span>{courseDetails?.sub_titles.length} Sections</span>
                    <span>{courseDetails?.duration} total length</span>
                    <span>Last update {courseDetails?.last_update}</span>
                  </div>
                  <div className={styles.courseLessonsSec}>
                    {[...(lessons || [])]
                      .sort((a, b) => a.id - b.id)
                      .map((lesson) => (
                        <div className={styles.titles} key={lesson.id}>

                          {/* LESSON HEADER */}
                          <div
                            className={styles.lessonTitle}
                            onClick={() => toggleDetails(lesson.id)}
                          >
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              style={{
                                transform: expandedLessons[lesson.id]
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                                transition: "0.3s",
                              }}
                            />
                            {lesson.title}
                          </div>

                          {/* LESSON CONTENT */}
                          {expandedLessons[lesson.id] &&
                            lesson.videos?.map((video) => (
                              <div className={styles.showBox} key={video.id}>

                                <div className={styles.descRow}>
                                  <div className={styles.descRowLeft}>
                                    <FontAwesomeIcon icon={faFileVideo} />
                                    <div className={styles.name} onClick={() => {video?.is_free && getPreviewVideos(video.id); setPreviewModalOpen(true) }}>
                                      {video.title}
                                    </div>
                                  </div>
                                  {video?.is_free && <span className={styles.previewBtn} onClick={() => { getPreviewVideos(video.id); setPreviewModalOpen(true) }}>Preview</span>}
                                </div>

                                {/* {video.doc_title && (
                                  <div className={styles.descRow}>
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    <div className={styles.name}>
                                      {video.doc_title}
                                    </div>
                                  </div>
                                )} */}

                              </div>
                            ))}
                        </div>
                      ))}
                  </div>

                  <div className={styles.additionalContent}>

                    <section className={styles.sectionCard}>
                      <p className={styles.sectionEyebrow}>Hands-on Projects You Will Build</p>
                      <div className={styles.projectGrid}>
                        {projects.map((project) => (
                          <div className={styles.projectCard} key={project}>{project}</div>
                        ))}
                      </div>
                    </section>

                    <section className={styles.sectionCard}>
                      <p className={styles.sectionEyebrow}>Technologies You Will Learn</p>
                      <div className={styles.techGrid}>
                        {technologies.map((group) => (
                          <div className={styles.techGroup} key={group.title}>
                            <h4>{group.title}</h4>
                            <ul className={styles.bulletListCompact}>
                              {group.items.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className={styles.sectionCard}>
                      <p className={styles.sectionEyebrow}>Frequently Asked Questions</p>
                      <div className={styles.faqList}>
                        {faqs.map((faq) => (
                          <div className={styles.faqItem} key={faq.question}>
                            <h4>{faq.question}</h4>
                            {faq.isHtml ? (
                              <p dangerouslySetInnerHTML={{ __html: faq.answer }} />
                            ) : (
                              <p>{faq.answer}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className={styles.ctaSection}>
                      <h3>Start your AI journey today</h3>
                      <p>Understand how AI works and build intelligent applications yourself.</p>
                      {isLoggedIn ? (
                        <></>
                      ) : (
                        <button
                          className={styles.ctaButton} type="button"
                          onClick={openLogin}
                        >
                          Enroll Now
                        </button>
                      )}
                    </section>
                  </div>
                </div>

                {/* RIGHT - B */}
                <aside className={styles.B}>
                  <img
                    src={courseDetails?.image_url}
                    className={styles.thumbImg}
                    alt="course"
                  />
                  <p className={styles.sidebarDesc}>
                    {courseDetails?.description?.split(".")[0]}
                  </p>
                  <div className={styles.mobileDetails}>
                    <p className={styles.titleMobile}>{courseDetails?.title}</p>

                    <p className={styles.imageLabel}>
                      {courseDetails?.course_category?.name}
                    </p>
                    <p className={styles.sidebarDescMobile}>
                      {courseDetails?.description?.split(".")[1]}
                    </p>
                    <p className={styles.autherMobile}>
                      Created by {courseDetails?.user?.full_name}
                    </p>
                    <div className={styles.detailsRowMobile}>
                      <p className={styles.detailsP}>
                        <FontAwesomeIcon icon={faUser} />
                        Enrolls {courseDetails?.download}
                      </p>
                      <p className={styles.detailsP}>
                        <FontAwesomeIcon icon={faMoneyBill} />
                        Rs. {courseDetails?.discount_price > 0 ? formatNumber(courseDetails?.discount_price) : formatNumber(courseDetails?.price)}
                        <span style={{ textDecoration: 'line-through', color: '#5B5A5A' }}>Rs. {courseDetails?.discount_price > 0 && courseDetails?.price?.toFixed(2)}</span>
                      </p>
                    </div>
                  </div>
                  {isLoggedIn ? (
                    // <PaymentForm course_id={courseDetails?.id} />
                    <div>
                      <p className={styles.payTitle}>Select your Payment Method</p>
                      <div className="payment-card" style={{ marginTop: '0' }}>
                        {/* Header */}
                        <label className="payment-header">
                          <input
                            type="radio"
                            name="payment"
                            value="Online"
                            checked={paymentType === "Online"}
                            onChange={(e) => {
                              setPaymentType("Online");
                              setPaymentTypeError("");
                            }}
                            className={`custom-radio`}
                          />

                          <span>Debit / Credit Card</span>

                          <div className="card-icons">
                            <img
                              src="https://img.icons8.com/color/48/visa.png"
                              alt="Visa"
                            />
                            <img
                              src="https://img.icons8.com/color/48/mastercard.png"
                              alt="MasterCard"
                            />
                            <img
                              src="https://img.icons8.com/color/48/amex.png"
                              alt="Amex"
                            />
                          </div>
                        </label>
                        <hr />
                        <label className="payment-header">
                          <input
                            type="radio"
                            name="payment"
                            value="Transfer"
                            checked={paymentType === "Transfer"}
                            onChange={(e) => {
                              setPaymentType("Transfer");
                              setPaymentTypeError("");
                            }}
                            className={`custom-radio`}
                          />
                          <span>Bank Transfer / Deposit</span>
                        </label>
                        {paymentType === "Transfer" && (
                          <>
                            <div>
                              <p className={styles.accTitle}>Payment Instructions</p>
                              {bankDetails.map((item, index) => (
                                <div key={index} className={styles.row}>
                                  <div>
                                    <p className={styles.value}>{item.label}: {item.value}</p>
                                  </div>
                                  {/* <button
                                    className={styles.copyBtn}
                                    onClick={() => handleCopy(item.value, index)}
                                  > */}
                                  {copiedIndex === index ? "Copied!" : <FontAwesomeIcon icon={faCopy} onClick={() => handleCopy(item.value, index)} className={styles.copyBtn} />}
                                  {/* </button> */}
                                </div>
                              ))}
                            </div>
                            <div
                              onDrop={handleDrop}
                              onDragOver={(event) => event.preventDefault()}
                              onClick={handleClick}
                              className={`drop-input`}
                            >
                              <input
                                type="file"
                                ref={fileInputRef}
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                              />
                              {!paySlip ? (
                                <p>Drag & Drop a file here or click to select</p>
                              ) : (
                                <div className={`inserted-file`}>
                                  <div className={`file-card`}>
                                    <img
                                      src={URL.createObjectURL(paySlip)}
                                      alt="File Type"
                                      className={`${styles.thumbnailImg}`}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            {paySlipError && (
                              <p className={`text-danger error-msg`}>
                                {paySlipError}
                              </p>
                            )}
                          </>
                        )}
                        <hr />
                        <label className="payment-header">
                          <input
                            type="radio"
                            name="payment"
                            value="WhatsApp"
                            checked={paymentType === "WhatsApp"}
                            onChange={(e) => {
                              setPaymentType("WhatsApp");
                              setPaymentTypeError("");
                            }}
                            className={`custom-radio`}
                          />
                          <span>WhatsApp</span>
                        </label>
                      </div>
                      {paymentTypeError && (
                        <p className={`text-danger error-msg`}>
                          {paymentTypeError}
                        </p>
                      )}
                      <div className={styles.payBtnSection}>
                        {paymentType === "" && <button
                          className={`primary-btn ${styles.purchaseBtn}`}
                          onClick={() => {
                            setPaymentTypeError("කරුණාකර ගෙවීම් ක්‍රමයක් තෝරන්න.")
                          }}
                        >
                          Purchase
                        </button>}
                        {paymentType === "Online" &&
                          <PaymentForm course_id={courseDetails?.id} />}
                        {paymentType === "Transfer" && <button
                          className={`primary-btn ${styles.purchaseBtn}`}
                          type="button"
                          onClick={submitPayment}
                          disabled={disabledBtn}
                        >
                          {disabledBtn ? <BeatLoader /> : `Submit`}
                        </button>}
                        {paymentType === "WhatsApp" &&
                          <button
                            className={`primary-btn ${styles.purchaseBtn}`}
                            type="button"
                            onClick={submitWhatsApp}
                            disabled={disabledBtn}
                          > WhatsApp </button>}
                      </div>
                    </div>
                  ) : (
                    <button
                      className={`primary-btn ${styles.purchaseBtn}`}
                      onClick={openLogin}
                    >
                      Purchase
                    </button>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </>
      )}
      {
        isPreviewModalOpen &&
        <div className={styles.overlay}>
          <div className={styles.modal} ref={modalRef}>
            <div className={styles.modalHeader}>
              <span>Course Preview</span>
              <FontAwesomeIcon icon={faXmark} onClick={() => setPreviewModalOpen(false)} size="lg" cursor="pointer" />
            </div>
            <p className={styles.modalTitle}>{courseDetails?.title}</p>
            <div className={styles.modalBody}>
              <div className={styles.videoWrapper}>
                <ReactPlayer
                  url={selectedPreview?.video_url}
                  width="100%"
                  height={300}
                  controls
                  playing={false}
                  playsinline
                  config={{
                    vimeo: {
                      playerOptions: {
                        autoplay: false,
                        muted: false,
                        loop: false,
                        title: false,
                        byline: false,
                        portrait: false,
                        speed: true,
                        transparent: false,
                      },
                    },
                  }}
                />
              </div>
              {previewVideos?.map((video) => (
                <div
                  className={`primary-box  ${styles.showBox} ${selectedPreview?.id ===
                    video.id
                    ? `${styles.activeBox}`
                    : ""
                    }`}
                  key={video.id}
                >
                  <div className={styles.descRowModal}>
                    <img
                      src={video?.image_url}
                      className={styles.thumbnailImg}
                      onClick={() => setSelectedPreview(video)}
                    />
                    <div
                      className={styles.name}
                      onClick={() => setSelectedPreview(video)}
                    >
                      <p className={styles.titleWidth}>
                        {video.title}
                      </p>
                      <p className={styles.descText}>
                        {video.description.length > 50
                          ? video.description.substring(0, 100) +
                          "..."
                          : video.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    </div >
  );
};
export default CourseGenAI;